// Weekly Audit history (GET /api/audit/history): past 7-day snapshots, read through the
// `taken_at` parameter of the Audit snapshot routes. Pure helpers only (no imports).

export interface AuditHistoryKind {
  kind: string;
  protocol: string | null;
}

export interface AuditHistoryEntry {
  taken_at: string;
  window: string;
  kinds: AuditHistoryKind[];
}

export interface AuditHistoryResponse {
  window: string;
  entries: AuditHistoryEntry[];
}

/** "Oct 11 2026, 02:09 MDT": date with year, 24-hour time and time zone, in the viewer's local time
 *  (a time zone can be passed for tests). The raw text when the time cannot be read. */
export function historyStamp(takenAt: string, timeZone?: string): string {
  const d = new Date(takenAt);
  if (Number.isNaN(d.getTime())) return takenAt;
  const parts = new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    timeZone, timeZoneName: "short",
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("month")} ${get("day")} ${get("year")}, ${get("hour")}:${get("minute")} ${get("timeZoneName")}`.trim();
}

/** "7 days to Oct 11 2026, 02:09 MDT", in the viewer's local time. */
export function historyLabel(takenAt: string, timeZone?: string): string {
  const when = historyStamp(takenAt, timeZone);
  return when === takenAt ? takenAt : `7 days to ${when}`;
}

/** "Snapshot: Oct 11 2026, 02:09 MDT": what a snapshot view says it is showing. */
export function snapshotStampText(takenAt: string, timeZone?: string): string {
  return `Snapshot: ${historyStamp(takenAt, timeZone)}`;
}

/** The instant relative times ("6s ago", "0h ago") are measured against: the snapshot's own time while a
 *  snapshot is shown, otherwise now. */
export function asOfMs(takenAt: string | null | undefined, nowMs: number = Date.now()): number {
  if (!takenAt) return nowMs;
  const t = Date.parse(takenAt);
  return Number.isNaN(t) ? nowMs : t;
}

const WEEKDAY_NAMES: Record<string, string> = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday",
  friday: "Friday", saturday: "Saturday", sunday: "Sunday",
};

/** The Settings label of a stored weekday value ("sunday" -> "Sunday"); null when it is not a weekday. The
 *  stored value is unchanged. */
export function weekdayLabel(raw: string): string | null {
  return WEEKDAY_NAMES[raw] ?? null;
}

/** The query fragment for a history read ("" when viewing live). */
export function historyQuery(takenAt: string | null | undefined): string {
  return takenAt ? `&taken_at=${encodeURIComponent(takenAt)}` : "";
}

/** A history snapshot is a 7-day result, so whatever window was chosen is 7d while viewing one. */
export function effectiveWindow<T extends string>(windowKey: T, takenAt: string | null | undefined): T | "7d" {
  return takenAt ? "7d" : windowKey;
}

/** Entries newest first (the API already sends them that way; kept here so the order is a rule). */
export function sortHistory(entries: AuditHistoryEntry[]): AuditHistoryEntry[] {
  return [...entries].sort((a, b) => (a.taken_at < b.taken_at ? 1 : a.taken_at > b.taken_at ? -1 : 0));
}

/** The window from a URL or saved view: the retired 30d reads as 7d. */
export function normalizeWindowParam(raw: string | null | undefined): string | null {
  if (!raw) return raw ?? null;
  return raw === "30d" ? "7d" : raw;
}
